import { useRef, useState } from "react";
import type { Msg } from "./types";
import { calculateOpenAiUsage } from "./calculateOpenAiUsage";
import { z } from "zod";
import { v4 as uuid } from "uuid";
import { useChatbotContext } from "./ChatbotContext";
import { STRINGS } from "./constants";

export type Payload = {
  messages: Msg[];
  blockId?: string;
};

export type Status = "idle" | "loading" | "error" | "complete";
export type CompletionStatus = {
  status: Status;
  code: string;
  predictionId: string;
};

const OpenAIChatCompletionResponseSchema = z.object({
  id: z.string(),
  object: z.string(),
  created: z.number(),
  model: z.string(),
  choices: z.array(
    z.object({
      index: z.number().int(),
      message: z.object({
        role: z.string(),
        content: z.string(),
      }),
      finish_reason: z.string().nullable(),
    })
  ),
  usage: z.object({
    prompt_tokens: z.number().int(),
    completion_tokens: z.number().int(),
    total_tokens: z.number().int(),
  }),
});

const ErrorResponseSchema = z
  .object({
    code: z.string(),
    error: z.string(),
    message: z.string().optional(),
    meta: z
      .object({
        code: z.string(),
        message: z.string(),
        param: z.string(),
        type: z.string(),
      })
      .partial()
      .passthrough()
      .optional(),
  })
  .passthrough();

const CODE_FOR_USER = Symbol("interfaces-chat-error-code");
const MESSAGE_FOR_USER = Symbol("interfaces-chat-error-message");
const chatError = function (message: string, code: string) {
  const error = Error(message);
  Object.defineProperty(error, CODE_FOR_USER, {
    get() {
      return code;
    },
  });
  Object.defineProperty(error, MESSAGE_FOR_USER, {
    get() {
      return message;
    },
  });
  // Omit frames above userError
  if (Error.captureStackTrace) Error.captureStackTrace(error, chatError);
  return error;
};
const getChatErrorCode = (e: any) => e[CODE_FOR_USER];
const getChatErrorMessage = (e: any) => e[MESSAGE_FOR_USER];

export function useChatCompletion(onResultUpdated?: (result: string) => void) {
  const {
    chatbotSessionId,
    config: { model },
    onUsage,
    fetcher,
  } = useChatbotContext();

  const resultRef = useRef("");
  // TODO pull this state up a level as part of container work to avoid need for onResultUpdated
  // https://zapierorg.atlassian.net/browse/INTRFCS-1124
  const [result, setResult] = useState("");

  async function runChatCompletion(
    payload: Payload
  ): Promise<CompletionStatus> {
    try {
      if (!payload.blockId) {
        throw new Error("Missing blockId");
      }

      setResult(() => "");
      onResultUpdated?.("");
      resultRef.current = "";

      const controller = new AbortController();
      const { signal } = controller;

      // TODO: zap function calls can take awhile, this timeout may need to
      // be increased in some cases, perhaps we explore some UX that would
      // allow longer timeout without too much pain?
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetcher("/api/chat/v2", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ...payload,
          chatbotSessionId,
        }),
        signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorResponse = await response.json();

        const parsedErrorResponse =
          ErrorResponseSchema.safeParse(errorResponse);

        if (parsedErrorResponse.success) {
          throw chatError(
            parsedErrorResponse.data.meta?.message ??
              parsedErrorResponse.data.error,
            parsedErrorResponse.data.meta?.code ?? parsedErrorResponse.data.code
          );
        }

        throw chatError(
          response.statusText,
          response.statusText.toLowerCase().replace(/\s/g, "_")
        );
      }

      const predictionId =
        response.headers.get("x-interfaces-prediction-id") ?? uuid();

      const contentType = response.headers.get("content-type");

      // The `fetcher` could return a JSON response instead of a stream
      // so we should handle that case differently... (ex: Storybook)
      if (contentType === "application/json") {
        const result = OpenAIChatCompletionResponseSchema.safeParse(
          await response.json()
        );
        if (!result.success) {
          throw chatError(
            result.error.issues[0].message ??
              STRINGS["Unable to parse OpenAI response with Zod schema"],
            result.error.issues[0].code ?? ""
          );
        }

        const {
          choices: [
            {
              message: { content },
            },
          ],
          usage,
        } = result.data;

        const decodedContent = content.replace(/^[\n\r]+/g, "");

        if (decodedContent) {
          resultRef.current = resultRef.current + decodedContent;
          setResult((prev) => {
            const res = prev + decodedContent;
            if (!res.startsWith("[ERROR]")) {
              onResultUpdated?.(res);
            }
            return res;
          });
        }

        onUsage({
          prompt_token_quantity: usage.prompt_tokens,
          completion_token_quantity: usage.completion_tokens,
        });
      } else {
        // We're dealing with a stream from the `fetcher` so handle like normal
        const data = response.body;

        if (!data) {
          throw chatError(STRINGS["No response was generated."], "no_data");
        }

        const reader = data.getReader();
        const decoder = new TextDecoder();

        let done = false;

        while (!done) {
          const { value, done: doneReading } = await reader.read();
          done = doneReading;

          const decodedValue = decoder.decode(value);

          if (decodedValue) {
            resultRef.current = resultRef.current + decodedValue;
            setResult((prev) => {
              const res = prev + decodedValue;
              if (!res.startsWith("[ERROR]")) {
                onResultUpdated?.(res);
              }
              return res;
            });
          }
        }

        onUsage(
          calculateOpenAiUsage(model, payload.messages, resultRef.current)
        );
      }

      if (resultRef.current.startsWith("[ERROR]")) {
        const { error, code } = ErrorResponseSchema.parse(
          JSON.parse(resultRef.current.replace("[ERROR]", "").trim())
        );
        resultRef.current = "";
        throw chatError(error ?? STRINGS["Unknown error occurred"], code);
      }

      if (resultRef.current.endsWith("[max_completion_limit]")) {
        resultRef.current = "";
        throw chatError(
          STRINGS["You've reached the chat completion limit for this session."],
          "max_completion_limit"
        );
      }

      return {
        status: "complete",
        code: "success",
        predictionId,
      };
    } catch (error) {
      console.error(error);

      const isAbortError =
        error instanceof DOMException && error.name === "AbortError";

      const errMessage = getChatErrorMessage(error);
      const hasChatError = Boolean(errMessage);

      const code =
        (isAbortError ? "timeout" : getChatErrorCode(error)) ?? "unknown";

      let newRef: string = STRINGS["Something went wrong."];
      if (isAbortError) {
        newRef =
          STRINGS[
            "Hmm, things are taking a bit too long. You can try again, or try a different question."
          ];
      } else if (hasChatError) {
        newRef = errMessage;
      }

      resultRef.current = newRef;

      return {
        status: "error",
        code,
        predictionId: uuid(),
      };
    }
  }

  return [runChatCompletion, resultRef, result] as const;
}
